package team.platforma.multiplatform_material_extension.scrollbar

import androidx.compose.animation.core.FastOutLinearInEasing
import androidx.compose.animation.core.FastOutSlowInEasing
import androidx.compose.animation.core.LinearEasing
import androidx.compose.animation.core.LinearOutSlowInEasing
import androidx.compose.animation.core.animateDpAsState
import androidx.compose.animation.core.tween
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.gestures.detectVerticalDragGestures
import androidx.compose.foundation.gestures.scrollBy
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.wrapContentHeight
import androidx.compose.foundation.layout.wrapContentSize
import androidx.compose.foundation.lazy.LazyListState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableFloatStateOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import kotlinx.coroutines.launch
import team.platforma.multiplatform_material_extension.scrollbar.style.ScrollIndicatorStyle

@Composable
fun VerticalScrollIndicator(
    state: LazyListState,
    threshold: Float = 250f,
    lazyColumn: @Composable () -> Unit
) {
    Box(
        modifier = Modifier.wrapContentSize()
    ) {
        lazyColumn()
        VerticalIndicator(
            state = state,
            modifier = Modifier
                .matchParentSize()
                .align(Alignment.CenterEnd),
            threshold = threshold,
        )
    }
}

@Composable
private fun VerticalIndicator(
    state: LazyListState,
    threshold: Float,
    modifier: Modifier = Modifier,
) {

    val style = ScrollIndicatorStyle

    val layoutInfo = state.layoutInfo
    val visibleItems = layoutInfo.visibleItemsInfo
    val totalItems = layoutInfo.totalItemsCount

    if (visibleItems.isEmpty() || totalItems == 0) return

    BoxWithConstraints(modifier = modifier){
        val viewportHeightPx = layoutInfo.viewportSize.height.toFloat()
//        val totalContentHeightPx = layoutInfo.totalItemsCount * visibleItems.first().size.toFloat() // old

        val averageItemHeightPx = visibleItems.map { it.size }.average().toFloat()
        val totalContentHeightPx = averageItemHeightPx * totalItems

        if (totalContentHeightPx <= viewportHeightPx) return@BoxWithConstraints

        val minScrollbarHeightPx = with(LocalDensity.current) { style.indicatorMinSize().toPx() }

//        val scrollbarHeightPx =
//            ((viewportHeightPx / totalContentHeightPx) * viewportHeightPx) // old
//                .coerceAtLeast(minScrollbarHeightPx)
//
//        val maxScroll = totalContentHeightPx - viewportHeightPx
//        val currentScroll = state.firstVisibleItemIndex * visibleItems.first().size + // old
//                state.firstVisibleItemScrollOffset
//
//        val scrollFraction = (currentScroll / maxScroll).coerceIn(0f, 1f) // old
//
//        val scrollbarTopPx = (scrollFraction * (viewportHeightPx - scrollbarHeightPx)) // old
//            .coerceIn(0f, viewportHeightPx - scrollbarHeightPx)

        val scrollbarHeightPx =
            ((viewportHeightPx / totalContentHeightPx) * viewportHeightPx)
                .coerceAtLeast(minScrollbarHeightPx)

        val maxScroll = totalContentHeightPx - viewportHeightPx
        val currentScroll = state.firstVisibleItemIndex * averageItemHeightPx +
                state.firstVisibleItemScrollOffset

        val scrollFraction = (currentScroll / maxScroll).coerceIn(0f, 1f)


        val maxScrollbarTop = maxOf(0f, viewportHeightPx - scrollbarHeightPx)
        val scrollbarTopPx = (scrollFraction * maxScrollbarTop).coerceIn(0f, maxScrollbarTop)

//        val scrollbarTopPx = (scrollFraction * (viewportHeightPx - scrollbarHeightPx))
//            .coerceIn(0f, viewportHeightPx - scrollbarHeightPx)

        val density = LocalDensity.current

        val scrollbarTopDp = with(density) { scrollbarTopPx.toDp() }

        val animHeight by animateDpAsState(
            targetValue = with(density) { scrollbarHeightPx.toDp() },
            animationSpec = tween(
                durationMillis = 200,
                easing = FastOutLinearInEasing
            )
        )

        val scrollDeltaBuffer = remember { mutableStateOf(0f) }

        val visualOffsetY by remember(scrollDeltaBuffer.value) {
            derivedStateOf {
                val elementHeight = visibleItems.firstOrNull()?.size?.toFloat() ?: 1f
                val percent = viewportHeightPx / elementHeight
                val correctionFactor = totalItems / percent
                val additionalPx = scrollDeltaBuffer.value / correctionFactor

                val maxOffset = maxOf(0f, viewportHeightPx - scrollbarHeightPx - scrollbarTopPx)
//                val maxOffset = viewportHeightPx - scrollbarHeightPx - scrollbarTopPx
                val minOffset = -scrollbarTopPx

                val clampedPx = additionalPx.coerceIn(minOffset, maxOffset)

                with(density) { clampedPx.toDp() }
            }
        }

        val coroutineScope = rememberCoroutineScope()


        var isPressed by remember { mutableStateOf(false) }

        val animatedWidth by animateDpAsState(
            targetValue = if (isPressed) 8.dp else 4.dp,
            animationSpec = tween(durationMillis = 200)
        )
        val animatedPadding by animateDpAsState(
            targetValue = if (isPressed) 4.dp else 6.dp,
            animationSpec = tween(durationMillis = 200)
        )

        var rawScroll by remember { mutableFloatStateOf(0f) }


        val animIndicatorPosition by animateDpAsState(
            targetValue =  scrollbarTopDp + visualOffsetY,
            animationSpec = tween(
                durationMillis = 300,
                easing = LinearOutSlowInEasing
            )
        )

        Box(
            modifier = Modifier
                .align(Alignment.TopEnd)
                .padding(end = animatedPadding)
                .wrapContentHeight()
                .width(14.dp)
                .offset(x = 0.dp, y = animIndicatorPosition)
                .then(
                    Modifier.pointerInput(Unit) {
                        detectVerticalDragGestures { change, dragAmount ->

                            coroutineScope.launch {
                                val layoutInfoNew = state.layoutInfo
                                val total = layoutInfoNew.totalItemsCount

                                val elementHeight =
                                    layoutInfoNew.visibleItemsInfo.firstOrNull()?.size?.toFloat()
                                        ?: return@launch

                                val percent = viewportHeightPx / elementHeight

                                val correctionFactor = total / percent

                                rawScroll = dragAmount * correctionFactor

                                if (kotlin.math.abs(rawScroll) > threshold) {
                                    scrollDeltaBuffer.value += rawScroll
                                } else {
                                    val totalScroll = rawScroll + scrollDeltaBuffer.value
                                    state.scrollBy(totalScroll)
                                    scrollDeltaBuffer.value = 0f
                                    rawScroll = 0f
                                }

                            }
                        }
                    }
                ).pointerInput(Unit) {
                    awaitPointerEventScope {
                        while (true) {
                            val event = awaitPointerEvent()
                            event.changes.forEach { change ->
                                isPressed = change.pressed
                                if (!isPressed) {
                                    coroutineScope.launch {
                                        val totalScroll = rawScroll + scrollDeltaBuffer.value
                                        state.scrollBy(totalScroll)
                                        scrollDeltaBuffer.value = 0f
                                        rawScroll = 0f
                                    }
                                }
                            }
                        }
                    }
                }
        ) {

            Box(
                Modifier
                    .heightIn(animHeight.coerceIn(style.indicatorMinSize(), Float.MAX_VALUE.dp))
                    .width(animatedWidth)
                    .padding(vertical = style.indicatorPadding())
                    .align(Alignment.TopEnd)
                    .shadow(elevation = style.getElevation(), shape = style.getShape())
                    .background(color = style.getColor(), shape = style.getShape())
            )

        }
    }
}




